import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AlertCircle, CheckCircle, Info, XCircle, X } from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Collapsible,
  CollapsibleContent,
} from '../../components/ui/collapsible';
import { removeAlertMessage } from './dashboardSlice';
import { RootState } from '../../reducers';

const AlertMessage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { dashboard } = useSelector((state: RootState) => state);
  const { alertMessageStack } = dashboard;
  const lastMessage = alertMessageStack[alertMessageStack.length - 1];
  const [isOpen, setIsOpen] = useState(false);

  const handleOnClick = () => {
    setIsOpen(false);
    setTimeout(() => {
      dispatch(removeAlertMessage());
    }, 150); // Wait for animation to complete
  };

  // Animate in when a new message appears
  useEffect(() => {
    if (lastMessage) {
      setIsOpen(true);
    }
  }, [lastMessage]);

  // Auto-dismiss success messages after 4 seconds
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'success') {
      console.log(
        'Setting up auto-dismiss timer for success message:',
        lastMessage.message,
      );
      const timer = setTimeout(() => {
        console.log('Auto-dismissing success message:', lastMessage.message);
        handleOnClick();
      }, 4000); // 4 seconds for success messages

      return () => {
        console.log('Clearing auto-dismiss timer for:', lastMessage.message);
        clearTimeout(timer);
      };
    }

    // Always return a cleanup function to satisfy ESLint consistent-return
    return () => {};
  }, [lastMessage]);

  // Auto-dismiss alert messages when navigating to a different page
  useEffect(() => {
    if (lastMessage && location.pathname) {
      console.log(
        'Navigation detected, dismissing alert:',
        lastMessage.message,
      );
      handleOnClick();
    }
  }, [location.pathname]);

  if (!lastMessage) return null;
  const { message, type, buttonText, link } = lastMessage;

  // Map MUI severity types to shadcn variants and icons
  const getAlertConfig = (severity: string) => {
    switch (severity) {
      case 'error':
        return {
          variant: 'destructive' as const,
          Icon: XCircle,
          bgClass: 'bg-red-50 dark:bg-red-950/20',
        };
      case 'success':
        return {
          variant: 'default' as const,
          Icon: CheckCircle,
          bgClass: 'bg-green-50 dark:bg-green-950/20',
        };
      case 'warning':
        return {
          variant: 'warning' as const,
          Icon: AlertCircle,
          bgClass: 'bg-yellow-50 dark:bg-yellow-950/20',
        };
      case 'info':
      default:
        return {
          variant: 'default' as const,
          Icon: Info,
          bgClass: 'bg-blue-50 dark:bg-blue-950/20',
        };
    }
  };

  const { variant, Icon, bgClass } = getAlertConfig(type || 'info');

  const handleButtonClick = (e: React.MouseEvent) => {
    if (!link) {
      e.preventDefault();
      handleOnClick();
    }
  };

  return (
    <Collapsible open={isOpen} className="w-full">
      <CollapsibleContent>
        <div className="p-4">
          <Alert variant={variant} className={bgClass}>
            <div className="flex items-center gap-3">
              <Icon className="h-4 w-4 flex-shrink-0" />
              <AlertDescription className="flex-1 leading-normal">
                {message}
                {buttonText && (
                  <a
                    href={link || '#'}
                    onClick={handleButtonClick}
                    className="ml-2 font-medium underline underline-offset-4 hover:no-underline"
                  >
                    {buttonText}
                  </a>
                )}
              </AlertDescription>
              <button
                type="button"
                onClick={handleOnClick}
                className="flex-shrink-0 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </Alert>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default AlertMessage;
